import { z } from 'zod';
import { SDK_LOG_LEVELS, type SdkLogLine } from '../sdk/debug-logs.ts';

/** Event User Log and SDK Debugger (BB-35). */
export const DEBUG_SURFACES = ['sdk', 'server'] as const;
export const DEBUG_SESSION_TARGETS = ['installation', 'external_id'] as const;
export const DEBUG_SESSION_MAX_MINUTES = 60;

export const EventLogQuery = z.object({
  after: z.uuid().optional(),
  profileId: z.uuid().optional(),
  externalId: z.string().max(128).optional(),
  installationId: z.uuid().optional(),
  surface: z.enum(DEBUG_SURFACES).optional(),
  appInstanceId: z.uuid().optional(),
  code: z.string().max(100).optional(),
  sessionId: z.uuid().optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type EventLogQuery = z.infer<typeof EventLogQuery>;

export interface DebugLogSummary {
  id: string;
  receivedAt: string;
  surface: (typeof DEBUG_SURFACES)[number];
  captureReason: 'event_log_group' | 'debug_session';
  debugSessionId: string | null;
  installationId: string | null;
  profileId: string | null;
  externalId: string | null;
  appInstanceId: string | null;
  sdkVersion: string | null;
  appVersion: string | null;
  httpStatus: number;
  problemCode: string | null;
  latencyMs: number;
  operationCount: number;
  errorCount: number;
  /** Operation types in order, e.g. `event:deposit_created`, `attributes`. */
  operations: string[];
  /** Error codes of rejected operations. */
  codes: string[];
  truncated: boolean;
}

export interface DebugLogEntry extends DebugLogSummary {
  /** The request as received (secrets redacted; personal data masked without `pii.view`). */
  request: Record<string, unknown>;
  results: unknown[];
  maskedPaths: string[];
}

export const RejectionsQuery = z.object({ days: z.coerce.number().int().min(1).max(90).default(7), code: z.string().max(100).optional(), appInstanceId: z.uuid().optional() });
export interface RejectionRow {
  surface: (typeof DEBUG_SURFACES)[number];
  code: string;
  appInstanceId: string | null;
  appVersion: string;
  sdkVersion: string;
  count: number;
  lastSeenAt: string;
  lastSample: Record<string, unknown> | null;
}

export const CreateDebugSessionBody = z.strictObject({
  targetKind: z.enum(DEBUG_SESSION_TARGETS),
  targetValue: z.string().trim().min(1).max(128),
  /** 5 to `DEBUG_SESSION_MAX_MINUTES`; longer answers `422 debug_session_too_long`. */
  minutes: z.number().int().min(5).max(24 * 60).default(30),
  note: z.string().trim().max(500).optional(),
  /** Also collect the SDK's own verbose log from the target's devices in an internal group (BB-157). */
  collectSdkLogs: z.boolean().default(false),
});
export type CreateDebugSessionBody = z.infer<typeof CreateDebugSessionBody>;

export interface DebugSessionView {
  id: string;
  targetKind: (typeof DEBUG_SESSION_TARGETS)[number];
  targetValue: string;
  note: string | null;
  createdBy: { id: string; email: string | null } | null;
  createdAt: string;
  expiresAt: string;
  endedAt: string | null;
  active: boolean;
  captured: number;
  collectSdkLogs: boolean;
  /** SDK log lines uploaded so far (BB-157). */
  sdkLogLines: number;
}

/** SDK log of a debug session (BB-157): `level` keeps that level and above; `q` searches tag and message. */
export const SdkLogQuery = z.object({ level: z.enum(SDK_LOG_LEVELS).optional(), q: z.string().trim().max(200).optional(), installationId: z.uuid().optional() });
export type SdkLogQuery = z.infer<typeof SdkLogQuery>;
/** At most this many lines per answer, the oldest first. */
export const SDK_LOG_VIEW_MAX = 5000;
export interface SdkLogLineView extends SdkLogLine {
  installationId: string;
  chunkIndex: number;
}

export const ExportQuery = z.object({ format: z.enum(['json', 'csv']).default('json') });
